import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format, parseISO, subDays } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { codeLabel } from '../../utils/format';

/*
 * Shift notes — the notes support workers write on their shifts.
 *
 * This is NOT "Employee Notes", which reads the HR file kept ABOUT a worker (warnings,
 * conversations, reviews) and is usually empty. The notes a worker WRITES had no report at all
 * until now, which is why looking for them came back with nothing.
 *
 * Two entry points onto the same records, because two different people come looking:
 *   Staff Shift Notes   — grouped by worker: everything this person wrote.
 *   Client Shift Notes  — grouped by participant: the shift-note record held on them.
 * Both carry both filters, so either question can be narrowed to the other.
 *
 * Every date here is the day the note COVERS. A note written up on the Tuesday for the previous
 * Friday's shift falls inside a Friday-to-Friday range, and the export comes out in that order.
 */

const fmtDate = (v) => {
  if (!v) return '–';
  try { return format(parseISO(String(v).slice(0, 10)), 'd MMM yyyy'); } catch { return '–'; }
};

const fmtTime = (v) => (v ? String(v).slice(0, 5) : '');

const STATUS_MAP = {
  booked: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
  pending: 'bg-amber-100 text-amber-700',
};

const statusBadge = (v) => (
  <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium capitalize',
    STATUS_MAP[v] || 'bg-slate-100 text-slate-600')}>
    {v || '–'}
  </span>
);

// A note recorded on a day other than the one it covers is worth flagging — not as a problem,
// but because anyone auditing the record will want to see it rather than work it out.
const dateCell = (v, row) => (
  <span className="whitespace-nowrap">
    {fmtDate(v)}
    {row.written_later && (
      <span className="block text-[11px] text-amber-600">written {fmtDate(row.recorded_on)}</span>
    )}
  </span>
);

const noteCell = (v) => (v
  ? <span title={v} className="text-xs whitespace-pre-wrap block max-w-md">{v.slice(0, 300)}{v.length > 300 ? '…' : ''}</span>
  : <span className="text-slate-400">–</span>);

const shiftCell = (v, row) => (v
  ? <span className="whitespace-nowrap">{fmtDate(v)}<span className="block text-[11px] text-slate-400">{fmtTime(row.start_time)}–{fmtTime(row.end_time)}</span></span>
  : <span className="text-slate-400">No shift</span>);

// Column order differs only in which name leads, so each report reads top-down as its own thing.
const COLS_BY_STAFF = [
  { key: 'staff_name', label: 'Staff' },
  { key: 'note_date', label: 'Note Date', render: dateCell },
  { key: 'client_name', label: 'Participant' },
  { key: 'shift_date', label: 'Shift', render: shiftCell },
  { key: 'shift_type', label: 'Service', render: (v) => codeLabel(v) || '–' },
  { key: 'shift_status', label: 'Shift Status', render: statusBadge },
  { key: 'note_type', label: 'Type', render: (v) => codeLabel(v) || '–' },
  { key: 'note_content', label: 'Note', render: noteCell },
  { key: 'mood_rating', label: 'Mood', render: (v) => (v ? `${v}/5` : '–') },
  { key: 'behaviour_rating', label: 'Behaviour', render: (v) => (v ? `${v}/5` : '–') },
  { key: 'attachment_count', label: 'Photos', render: (v) => (v > 0 ? v : '–') },
];

const COLS_BY_CLIENT = [
  COLS_BY_STAFF[2], COLS_BY_STAFF[1], COLS_BY_STAFF[0],
  ...COLS_BY_STAFF.slice(3),
];

function ShiftNotes({ mode }) {
  const byStaff = mode === 'staff';
  const [data, setData] = useState([]);
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStart] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEnd] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [clientId, setClientId] = useState('');
  const [staffId, setStaffId] = useState('');
  const [noteType, setNoteType] = useState('');
  const pagination = usePagination(data);

  useEffect(() => {
    api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/staff').then(r => setStaff(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (clientId) p.set('client_id', clientId);
    if (staffId) p.set('staff_id', staffId);
    if (noteType) p.set('note_type', noteType);
    api.get(`/reports/${byStaff ? 'staff' : 'client'}-shift-notes?${p}`)
      .then(r => { setData(r.data || []); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, clientId, staffId, noteType, byStaff]);

  useEffect(() => { load(); }, [load]);

  // Offer only the note types that actually appear in what came back, so no filter is a dead end.
  const noteTypes = useMemo(
    () => [...new Set(data.map(r => r.note_type).filter(Boolean))].sort(),
    [data],
  );

  const selClient = clients.find(c => String(c.id) === String(clientId));
  const selStaff = staff.find(s => String(s.id) === String(staffId));

  const chips = [
    { label: 'Date Range', value: `${startDate} — ${endDate}` },
    ...(selStaff ? [{ label: 'Staff', value: `${selStaff.first_name} ${selStaff.last_name}`, onRemove: () => setStaffId('') }] : []),
    ...(selClient ? [{ label: 'Participant', value: `${selClient.first_name} ${selClient.last_name}`, onRemove: () => setClientId('') }] : []),
    ...(noteType ? [{ label: 'Type', value: codeLabel(noteType), onRemove: () => setNoteType('') }] : []),
  ];

  const lateCount = data.filter(r => r.written_later).length;
  const people = new Set(data.map(r => (byStaff ? r.staff_name : r.client_name)).filter(Boolean)).size;

  // The export takes whatever the filters left behind — filter first, then download, and the
  // file matches the screen.
  const title = byStaff
    ? (selStaff ? `Shift Notes — ${selStaff.first_name} ${selStaff.last_name}` : 'Staff Shift Notes — All Staff')
    : (selClient ? `Shift Notes — ${selClient.first_name} ${selClient.last_name}` : 'Client Shift Notes — All Participants');

  const COLS = byStaff ? COLS_BY_STAFF : COLS_BY_CLIENT;

  return (
    <ReportLayout
      title={title}
      data={data}
      filename={byStaff ? 'staff_shift_notes' : 'client_shift_notes'}
      columns={COLS}
      activeChips={chips}
      showFiltersPanel={showFilters}
      onToggleFilters={() => setShowFilters(v => !v)}
      filters={
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStart(e.target.value)} className="input-field !w-auto text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={e => setEnd(e.target.value)} className="input-field !w-auto text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Staff</label>
            <select value={staffId} onChange={e => setStaffId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Staff</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Participant</label>
            <select value={clientId} onChange={e => setClientId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Participants</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Note Type</label>
            <select value={noteType} onChange={e => setNoteType(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Types</option>
              {noteTypes.map(t => <option key={t} value={t}>{codeLabel(t)}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <button onClick={load} className="btn-primary text-sm px-4 py-2">Apply</button>
          </div>
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-6 text-sm text-slate-600 font-medium">
        <span>Notes: <strong className="text-slate-900">{data.length}</strong></span>
        <span>{byStaff ? 'Staff' : 'Participants'}: <strong className="text-slate-900">{people}</strong></span>
        {lateCount > 0 && (
          <span className="text-amber-600">
            Written up after the day they cover: <strong>{lateCount}</strong>
          </span>
        )}
      </div>
      <ReportTable
        columns={COLS}
        data={data}
        loading={loading}
        pagination={pagination}
        emptyMsg="No shift notes were written in this period."
      />
    </ReportLayout>
  );
}

export function StaffShiftNotesReport() { return <ShiftNotes mode="staff" />; }
export function ClientShiftNotesReport() { return <ShiftNotes mode="client" />; }
export default StaffShiftNotesReport;
